/**
 * Bestelling Formulier - Nieuwe/bewerk bestelling
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { bestellingenService, Bestelling, BestellingStatus } from '../../services/bestellingen.service';
import { relationsService, Relation } from '../../services/relations.service';
import { voorraadService } from '../../services/voorraad.service';
import { werkordersService, ActieveWerkorder, WerkorderSectie } from '../../services/werkorders.service';
import Toast from '../../components/Toast';

const BestellingForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const onderdeelId = searchParams.get('onderdeel_id');
  const onderdeelIds = searchParams.get('onderdeel_ids'); // Meerdere IDs komma gescheiden
  const isNew = !id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [bestelling, setBestelling] = useState<Bestelling | null>(null);
  const [leveranciers, setLeveranciers] = useState<Relation[]>([]);
  const [producten, setProducten] = useState<any[]>([]);
  const [filteredProducten, setFilteredProducten] = useState<any[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductResults, setShowProductResults] = useState(false);

  // Handmatig toevoegen
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualOmschrijving, setManualOmschrijving] = useState('');
  const [manualArtikelnummer, setManualArtikelnummer] = useState('');
  const [manualAantal, setManualAantal] = useState(1);
  const [manualStukprijs, setManualStukprijs] = useState('');
  const [manualWerkorderId, setManualWerkorderId] = useState<number | ''>('');
  const [actieveWerkorders, setActieveWerkorders] = useState<ActieveWerkorder[]>([]);
  const [secties, setSecties] = useState<WerkorderSectie[]>([]);
  const [manualSectieId, setManualSectieId] = useState<number | ''>('');

  // Zoekbare leverancier dropdown
  const [leverancierSearch, setLeverancierSearch] = useState('');
  const [showLeverancierDropdown, setShowLeverancierDropdown] = useState(false);

  // Editable fields
  const [leverancierId, setLeverancierId] = useState<number | ''>('');
  const [status, setStatus] = useState<BestellingStatus>('concept');
  const [notities, setNotities] = useState('');
  const [verwachteLeverdatum, setVerwachteLeverdatum] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Load leveranciers en actieve werkorders
      const [leveranciersData, werkordersData] = await Promise.all([
        relationsService.getAll({ type: 'leverancier' }),
        werkordersService.getActieveWerkorders()
      ]);
      setLeveranciers(leveranciersData.items);
      setActieveWerkorders(werkordersData);

      // Load producten voor dropdown
      const productenData = await voorraadService.searchItems();
      setProducten(productenData);

      if (id) {
        // Load existing bestelling
        const bestellingData = await bestellingenService.getById(parseInt(id));
        setBestelling(bestellingData);

        // Try to match leverancier_naam with a relation if no leverancier_id
        if (bestellingData.leverancier_id) {
          setLeverancierId(bestellingData.leverancier_id);
          // Vul leverancierSearch met de naam
          const lev = leveranciersData.items.find(l => l.id === bestellingData.leverancier_id);
          if (lev) {
            setLeverancierSearch(lev.bedrijfsnaam || `${lev.voornaam} ${lev.achternaam}`);
          }
        } else if (bestellingData.leverancier_naam) {
          // Try to find matching leverancier by name
          const matchedLev = leveranciersData.items.find(
            lev => (lev.bedrijfsnaam?.toLowerCase() === bestellingData.leverancier_naam?.toLowerCase()) ||
                   (`${lev.voornaam} ${lev.achternaam}`.toLowerCase() === bestellingData.leverancier_naam?.toLowerCase())
          );
          if (matchedLev) {
            setLeverancierId(matchedLev.id);
            setLeverancierSearch(matchedLev.bedrijfsnaam || `${matchedLev.voornaam} ${matchedLev.achternaam}`);
          }
        }

        setStatus(bestellingData.status);
        setNotities(bestellingData.notities || '');
        if (bestellingData.verwachte_leverdatum) {
          setVerwachteLeverdatum(bestellingData.verwachte_leverdatum.split('T')[0]);
        }
      } else if (onderdeelIds) {
        // Create new bestelling with multiple onderdelen
        const ids = onderdeelIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));

        if (ids.length === 0) {
          setError('Geen geldige onderdeel IDs gevonden');
          setLoading(false);
          return;
        }

        // Haal alle items op
        const items = await Promise.all(ids.map(id => voorraadService.getItemById(id)));

        // Bepaal leverancier (pak de eerste met een leverancier)
        const leverancierNaam = items.find(item => item.leverancier)?.leverancier || null;

        // Create bestelling with all onderdelen as regels
        const newBestelling = await bestellingenService.create({
          leverancier_id: null,
          leverancier_naam: leverancierNaam,
          status: 'concept',
          regels: items.map(item => ({
            voorraad_item_id: item.id,
            omschrijving: `${item.onderdeel_type || ''} ${item.model || ''} ${item.fabrikant || ''}`.trim(),
            artikelnummer: item.artikelnummer || undefined,
            aantal_besteld: 1,
            stukprijs: item.inkoopprijs || 0,
            btw_percentage: 21
          }))
        });

        // Navigate to the created bestelling
        navigate(`/bestellingen/${newBestelling.id}`, { replace: true });
        return;
      } else if (onderdeelId) {
        // Create new bestelling with single onderdeel
        const item = await voorraadService.getItemById(parseInt(onderdeelId));

        // Create bestelling with the onderdeel as first regel
        const newBestelling = await bestellingenService.create({
          leverancier_id: null,
          leverancier_naam: item.leverancier || null,
          status: 'concept',
          regels: [{
            voorraad_item_id: item.id,
            omschrijving: `${item.onderdeel_type || ''} ${item.model || ''} ${item.fabrikant || ''}`.trim(),
            artikelnummer: item.artikelnummer || undefined,
            aantal_besteld: 1,
            stukprijs: item.inkoopprijs || 0,
            btw_percentage: 21
          }]
        });

        // Navigate to the created bestelling
        navigate(`/bestellingen/${newBestelling.id}`, { replace: true });
        return;
      } else if (isNew) {
        // Create empty new bestelling
        const newBestelling = await bestellingenService.create({
          leverancier_id: null,
          leverancier_naam: null,
          status: 'concept',
          regels: []
        });

        // Navigate to the created bestelling
        navigate(`/bestellingen/${newBestelling.id}`, { replace: true });
        return;
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden gegevens');
    } finally {
      setLoading(false);
    }
  }, [id, onderdeelId, onderdeelIds, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!bestelling) return;

    // Valideer verplichte velden
    if (!leverancierId) {
      setError('Selecteer een leverancier');
      return;
    }

    try {
      setSaving(true);
      await bestellingenService.update(bestelling.id, {
        leverancier_id: leverancierId || null,
        status,
        notities: notities || null,
        verwachte_leverdatum: verwachteLeverdatum || null
      });
      navigate('/bestelpool');
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
      setSaving(false);
    }
  };

  const handleTerugNaarBestelpool = async () => {
    if (!bestelling) return;
    if (!window.confirm('Weet je zeker dat je deze bestelling terug wilt zetten naar de bestelpool?')) return;

    try {
      setSaving(true);
      await bestellingenService.update(bestelling.id, {
        status: 'concept'
      });
      navigate('/bestelpool');
    } catch (err: any) {
      setError(err.message || 'Fout bij terugzetten naar bestelpool');
      setSaving(false);
    }
  };

  const handleDeleteRegel = async (regelId: number) => {
    if (!bestelling) return;
    if (!window.confirm('Weet je zeker dat je deze regel wilt verwijderen?')) return;

    try {
      await bestellingenService.deleteRegel(bestelling.id, regelId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen regel');
    }
  };

  const handleUpdateRegel = async (regelId: number, field: string, value: any) => {
    if (!bestelling) return;

    try {
      await bestellingenService.updateRegel(bestelling.id, regelId, { [field]: value });
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij bijwerken regel');
    }
  };

  const handleBestellen = () => {
    navigate('/bestelpool');
  };

  const handleAllesOntvangen = async () => {
    if (!bestelling) return;

    try {
      setSaving(true);
      // Update alle regels met aantal_ontvangen = aantal_besteld
      for (const regel of bestelling.regels) {
        if ((regel.aantal_ontvangen || 0) < regel.aantal_besteld) {
          await bestellingenService.updateRegel(bestelling.id, regel.id!, {
            aantal_ontvangen: regel.aantal_besteld
          });
        }
      }
      // Update status naar ontvangen
      await bestellingenService.update(bestelling.id, { status: 'ontvangen' });
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij binnenboeken');
    } finally {
      setSaving(false);
    }
  };

  // Filter producten op zoekterm
  useEffect(() => {
    if (productSearch.trim().length === 0) {
      setFilteredProducten([]);
      return;
    }

    const term = productSearch.toLowerCase();
    const filtered = producten.filter(item =>
      (item.onderdeel_type && item.onderdeel_type.toLowerCase().includes(term)) ||
      (item.fabrikant && item.fabrikant.toLowerCase().includes(term)) ||
      (item.model && item.model.toLowerCase().includes(term)) ||
      (item.artikelnummer && item.artikelnummer.toLowerCase().includes(term)) ||
      (item.leverancier && item.leverancier.toLowerCase().includes(term))
    );

    setFilteredProducten(filtered);
  }, [productSearch, producten]);

  // Laad secties wanneer werkorder wijzigt
  useEffect(() => {
    const loadSecties = async () => {
      if (manualWerkorderId) {
        try {
          const taken = await werkordersService.getWerkorderTaken(manualWerkorderId);
          setSecties(taken.secties || []);
          setManualSectieId('');
        } catch (err) {
          setSecties([]);
        }
      } else {
        setSecties([]);
        setManualSectieId('');
      }
    };
    loadSecties();
  }, [manualWerkorderId]);

  const handleAddProduct = async (product: any) => {
    if (!bestelling || !product) return;

    // Valideer verplichte velden
    if (!leverancierId) {
      setError('Selecteer eerst een leverancier');
      return;
    }

    try {
      // Sla alle lokale wijzigingen op voordat we de regel toevoegen
      await bestellingenService.update(bestelling.id, {
        leverancier_id: leverancierId || null,
        notities: notities || null,
        verwachte_leverdatum: verwachteLeverdatum || null
      });
      await bestellingenService.addRegel(bestelling.id, {
        voorraad_item_id: product.id,
        omschrijving: `${product.onderdeel_type || ''} ${product.model || ''} ${product.fabrikant || ''}`.trim(),
        artikelnummer: product.artikelnummer || undefined,
        aantal_besteld: 1,
        stukprijs: product.inkoopprijs || 0,
        btw_percentage: 21
      });
      setProductSearch('');
      setShowProductResults(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij toevoegen product');
    }
  };

  const handleAddManualProduct = async () => {
    if (!bestelling || !manualOmschrijving.trim()) return;

    // Valideer verplichte velden
    if (!leverancierId) {
      setError('Selecteer eerst een leverancier');
      return;
    }

    try {
      // Sla alle lokale wijzigingen op voordat we de regel toevoegen
      await bestellingenService.update(bestelling.id, {
        leverancier_id: leverancierId || null,
        notities: notities || null,
        verwachte_leverdatum: verwachteLeverdatum || null
      });

      // Zoek klant_id via werkorder
      let klantId: number | undefined = undefined;
      if (manualWerkorderId) {
        const werkorder = actieveWerkorders.find(wo => wo.id === manualWerkorderId);
        if (werkorder) {
          klantId = werkorder.klant_id;
        }
      }

      await bestellingenService.addRegel(bestelling.id, {
        voorraad_item_id: undefined,
        klant_id: klantId,
        omschrijving: manualOmschrijving.trim(),
        artikelnummer: manualArtikelnummer || undefined,
        aantal_besteld: manualAantal,
        stukprijs: manualStukprijs ? parseFloat(manualStukprijs) : 0,
        btw_percentage: 21
      });
      // Reset form
      setManualOmschrijving('');
      setManualArtikelnummer('');
      setManualAantal(1);
      setManualStukprijs('');
      setManualWerkorderId('');
      setManualSectieId('');
      setShowManualAdd(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij toevoegen product');
    }
  };

  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  if (!bestelling && !isNew) {
    return (
      <div className="page-container">
        <div className="error-message">Bestelling niet gevonden</div>
        <button className="btn btn-secondary" onClick={() => navigate('/bestelpool')}>
          Terug naar Bestelpool
        </button>
      </div>
    );
  }

  // Show creating message if we're making a new bestelling
  if (isNew && !bestelling) {
    return (
      <div className="page-container">
        <div className="loading">Nieuwe bestelling aanmaken...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{bestelling?.status === 'concept' ? 'Bestelling Toevoegen' : (bestelling?.bestelnummer || 'Bestelling')}</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {bestelling?.status === 'besteld' && (
            <button
              className="btn btn-success"
              onClick={handleAllesOntvangen}
              disabled={saving}
              style={{ backgroundColor: '#10b981', color: 'white', border: 'none' }}
            >
              Alles Ontvangen
            </button>
          )}
          {bestelling?.status !== 'concept' && (
            <button
              className="btn btn-warning"
              onClick={handleTerugNaarBestelpool}
              disabled={saving}
              style={{ backgroundColor: '#f59e0b', color: 'white', border: 'none' }}
            >
              Terug naar Bestelpool
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      <Toast message={error} type="error" onClose={() => setError(null)} autoClose={0} />

      {bestelling && (
        <>
          {/* Bestelling Info */}
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Bestelgegevens</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                  Leverancier *
                </label>
                <input
                  type="text"
                  value={leverancierSearch}
                  onChange={(e) => {
                    setLeverancierSearch(e.target.value);
                    setShowLeverancierDropdown(true);
                    // Als de tekst niet meer overeenkomt met de geselecteerde leverancier, reset
                    const selectedLev = leveranciers.find(l => l.id === leverancierId);
                    const selectedName = selectedLev ? (selectedLev.bedrijfsnaam || `${selectedLev.voornaam} ${selectedLev.achternaam}`) : '';
                    if (e.target.value !== selectedName) {
                      setLeverancierId('');
                    }
                  }}
                  onFocus={() => setShowLeverancierDropdown(true)}
                  onBlur={() => setTimeout(() => setShowLeverancierDropdown(false), 200)}
                  className="form-control"
                  placeholder="Zoek leverancier..."
                  style={{ width: '100%' }}
                />
                {showLeverancierDropdown && leverancierSearch && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000
                  }}>
                    {leveranciers
                      .filter(lev => {
                        const naam = lev.bedrijfsnaam || `${lev.voornaam} ${lev.achternaam}`;
                        return naam.toLowerCase().includes(leverancierSearch.toLowerCase());
                      })
                      .map(lev => (
                        <div
                          key={lev.id}
                          onClick={() => {
                            setLeverancierId(lev.id);
                            setLeverancierSearch(lev.bedrijfsnaam || `${lev.voornaam} ${lev.achternaam}`);
                            setShowLeverancierDropdown(false);
                          }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          {lev.bedrijfsnaam || `${lev.voornaam} ${lev.achternaam}`}
                        </div>
                      ))}
                    {leveranciers.filter(lev => {
                      const naam = lev.bedrijfsnaam || `${lev.voornaam} ${lev.achternaam}`;
                      return naam.toLowerCase().includes(leverancierSearch.toLowerCase());
                    }).length === 0 && (
                      <div style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>
                        Geen leveranciers gevonden
                      </div>
                    )}
                  </div>
                )}
                {!leverancierId && bestelling.leverancier_naam && (
                  <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
                    Uit artikel: {bestelling.leverancier_naam}
                  </div>
                )}
              </div>
              {bestelling.status !== 'concept' && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as BestellingStatus)}
                    className="form-control"
                    style={{ width: '100%' }}
                  >
                    <option value="besteld">Besteld</option>
                    <option value="ontvangen">Ontvangen</option>
                    <option value="geannuleerd">Geannuleerd</option>
                  </select>
                </div>
              )}
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                  Verwachte leverdatum
                </label>
                <input
                  type="date"
                  value={verwachteLeverdatum}
                  onChange={(e) => setVerwachteLeverdatum(e.target.value)}
                  className="form-control"
                />
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                Notities
              </label>
              <textarea
                value={notities}
                onChange={(e) => setNotities(e.target.value)}
                className="form-control"
                rows={3}
                style={{ width: '100%' }}
                placeholder="Opmerkingen..."
              />
            </div>
          </div>

          {/* Regels */}
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Bestelregels</h3>

            {/* Product zoeken en toevoegen - alleen voor concept bestellingen */}
            {bestelling.status === 'concept' && (
              <div style={{ position: 'relative', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowManualAdd(!showManualAdd)}
                    style={{ whiteSpace: 'nowrap', height: '38px' }}
                  >
                    {showManualAdd ? 'Annuleren' : '+ Handmatig'}
                  </button>
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setShowProductResults(true);
                    }}
                    onFocus={() => setShowProductResults(true)}
                    className="form-control"
                    placeholder="Zoek op type, fabrikant, model, leverancier of artikelnummer..."
                    style={{ flex: 1 }}
                  />
                  {productSearch && (
                    <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {filteredProducten.length} {filteredProducten.length === 1 ? 'resultaat' : 'resultaten'}
                    </span>
                  )}
                </div>

                {/* Handmatig toevoegen formulier */}
                {showManualAdd && (
                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '6px',
                    marginTop: '0.5rem'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1.5fr auto', gap: '0.5rem', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Omschrijving *</label>
                        <input
                          type="text"
                          value={manualOmschrijving}
                          onChange={(e) => setManualOmschrijving(e.target.value)}
                          className="form-control"
                          placeholder="Productomschrijving..."
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Artikelnr.</label>
                        <input
                          type="text"
                          value={manualArtikelnummer}
                          onChange={(e) => setManualArtikelnummer(e.target.value)}
                          className="form-control"
                          placeholder="Optioneel"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Aantal</label>
                        <input
                          type="number"
                          value={manualAantal}
                          onChange={(e) => setManualAantal(parseInt(e.target.value) || 1)}
                          className="form-control"
                          min="1"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Stukprijs</label>
                        <input
                          type="number"
                          value={manualStukprijs}
                          onChange={(e) => setManualStukprijs(e.target.value)}
                          className="form-control"
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Voor werkorder</label>
                        <select
                          value={manualWerkorderId}
                          onChange={(e) => setManualWerkorderId(e.target.value ? parseInt(e.target.value) : '')}
                          className="form-control"
                        >
                          <option value="">Geen (voorraad)</option>
                          {actieveWerkorders.map(wo => (
                            <option key={wo.id} value={wo.id}>
                              {wo.werkordernummer} - {wo.klant}
                            </option>
                          ))}
                        </select>
                      </div>
                      {manualWerkorderId && secties.length > 0 && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Calculatie</label>
                          <select
                            value={manualSectieId}
                            onChange={(e) => setManualSectieId(e.target.value ? parseInt(e.target.value) : '')}
                            className="form-control"
                          >
                            <option value="">Selecteer calculatie...</option>
                            {secties.map(sectie => (
                              <option key={sectie.id} value={sectie.id}>
                                {sectie.naam}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddManualProduct}
                        disabled={!manualOmschrijving.trim()}
                      >
                        Toevoegen
                      </button>
                    </div>
                  </div>
                )}
                {showProductResults && filteredProducten.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000
                  }}>
                    {filteredProducten.map(product => (
                      <div
                        key={product.id}
                        onClick={() => handleAddProduct(product)}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {product.onderdeel_type} {product.model || ''} {product.fabrikant || ''}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            {product.artikelnummer && <span style={{ marginRight: '1rem' }}>Art: {product.artikelnummer}</span>}
                            {product.leverancier && <span>Lev: {product.leverancier}</span>}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '0.85rem' }}>
                          {product.inkoopprijs ? formatCurrency(product.inkoopprijs) : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {showProductResults && productSearch.length >= 1 && filteredProducten.length === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '1rem',
                    color: '#6b7280',
                    zIndex: 1000
                  }}>
                    Geen producten gevonden
                  </div>
                )}
              </div>
            )}

            {bestelling.regels.length === 0 ? (
              <p style={{ color: '#6b7280' }}>Geen regels toegevoegd</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: 600 }}>Artikelnr.</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: 600 }}>Omschrijving</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: 600 }}>Besteld</th>
                      {bestelling.status !== 'concept' && (
                        <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: 600 }}>Ontvangen</th>
                      )}
                      <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Stukprijs</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Totaal</th>
                      {bestelling.status === 'concept' && (
                        <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: 600 }}>Acties</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {bestelling.regels.map((regel) => (
                      <tr key={regel.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '0.75rem' }}>{regel.artikelnummer || '-'}</td>
                        <td style={{ padding: '0.75rem' }}>{regel.omschrijving}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          {bestelling.status === 'concept' ? (
                            <input
                              type="number"
                              defaultValue={regel.aantal_besteld}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value) || 1;
                                if (newValue !== regel.aantal_besteld) {
                                  handleUpdateRegel(regel.id!, 'aantal_besteld', newValue);
                                }
                              }}
                              style={{ width: '80px', textAlign: 'center' }}
                              className="form-control"
                              min="1"
                              max="10000"
                            />
                          ) : (
                            regel.aantal_besteld
                          )}
                        </td>
                        {bestelling.status !== 'concept' && (
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <input
                              type="number"
                              defaultValue={regel.aantal_ontvangen || 0}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue !== (regel.aantal_ontvangen || 0)) {
                                  handleUpdateRegel(regel.id!, 'aantal_ontvangen', newValue);
                                }
                              }}
                              style={{
                                width: '80px',
                                textAlign: 'center',
                                backgroundColor: (regel.aantal_ontvangen || 0) >= regel.aantal_besteld ? '#d1fae5' : '#fef3c7'
                              }}
                              className="form-control"
                              min="0"
                              max={regel.aantal_besteld}
                            />
                          </td>
                        )}
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(regel.stukprijs)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(regel.regel_totaal)}</td>
                        {bestelling.status === 'concept' && (
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={handleBestellen}
                              >
                                Bestellen
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteRegel(regel.id!)}
                              >
                                Verwijderen
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 600 }}>
                      <td colSpan={bestelling.status !== 'concept' ? 5 : 4} style={{ padding: '0.75rem', textAlign: 'right' }}>Totaal incl. BTW:</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(bestelling.totaal_incl_btw)}</td>
                      {bestelling.status === 'concept' && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BestellingForm;
